import { Dices, LogOut, WifiOff } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getOptionsForStage, isSelectionStage, stageIndex } from '@shared/game/logic';
import { RANDOM_BUTTON_LABELS, STAGE_LABELS, type SelectOption, type SelectionStage } from '@shared/types/room';
import { OptionGrid } from '@/components/OptionGrid';
import { ParticipantList } from '@/components/ParticipantList';
import { SelectionPath } from '@/components/SelectionPath';
import { StepProgress } from '@/components/StepProgress';
import { useCountdown } from '@/hooks/useCountdown';
import { useRandomReveal } from '@/hooks/useRandomReveal';
import { useRoomStore } from '@/stores/roomStore';

export function GameView() {
  const navigate = useNavigate();
  const state = useRoomStore((s) => s.state);
  const selfId = useRoomStore((s) => s.selfId);
  const reveal = useRoomStore((s) => s.reveal);
  const finishReveal = useRoomStore((s) => s.finishReveal);
  const send = useRoomStore((s) => s.send);
  const leave = useRoomStore((s) => s.leave);
  const [pending, setPending] = useState(false);
  const { highlightId, landed } = useRandomReveal(reveal);
  const deadline = useCountdown(reveal ? null : (state?.chooserDeadline ?? null));

  // 공개 애니메이션이 끝나면 최신 상태로 전환
  useEffect(() => {
    if (!reveal) return;
    const remaining = Math.max(0, reveal.startedAt + reveal.durationMs - Date.now());
    const id = setTimeout(finishReveal, remaining);
    return () => clearTimeout(id);
  }, [reveal, finishReveal]);

  // 상태가 바뀌면 선택 대기 상태 해제
  useEffect(() => setPending(false), [state?.version]);

  // 화면에 표시할 단계/후보: 공개 애니메이션 중에는 직전 단계를 유지
  const display = useMemo(() => {
    if (!state) return null;
    if (reveal) {
      const before = state.selections.filter((s) => stageIndex(s.stage) < stageIndex(reveal.stage));
      return { stage: reveal.stage, options: getOptionsForStage(reveal.stage, before), selections: before, chooserNickname: reveal.participantNickname, chooserId: null as string | null };
    }
    if (!isSelectionStage(state.stage)) return null;
    return { stage: state.stage, options: state.options, selections: state.selections, chooserNickname: state.currentParticipantNickname, chooserId: state.currentParticipantId };
  }, [state, reveal]);

  if (!state || !display) return null;

  const stage: SelectionStage = display.stage;
  const isMyTurn = !reveal && display.chooserId === selfId;
  const labels = STAGE_LABELS[stage];
  const chooser = state.participants.find((p) => p.id === state.currentParticipantId);
  const chooserOffline = !reveal && chooser && !chooser.connected;
  const canAct = isMyTurn && !pending && state.status === 'PLAYING';

  const handleSelect = (option: SelectOption) => {
    if (!canAct) return;
    setPending(true);
    send({ type: 'select', stage, optionId: option.id });
  };
  const handleRandom = () => {
    if (!canAct) return;
    setPending(true);
    send({ type: 'random', stage });
  };
  const handleLeave = () => {
    if (confirm('게임 도중 나가면 다시 참여할 수 없어요. 나갈까요?')) {
      leave();
      navigate('/');
    }
  };

  return (
    <div className="flex flex-col gap-4 animate-fade-up">
      <div className="card p-5 sm:p-6">
        <StepProgress stage={stage} />

        <div className="mt-5 min-h-16" aria-live="polite">
          {reveal ? (
            <div className="rounded-2xl bg-cream-100 px-4 py-3 text-center">
              <p className="font-bold">
                {reveal.isAuto
                  ? `${reveal.participantNickname}님의 연결이 끊어져 자동으로 선택했어요`
                  : reveal.isRandom
                    ? `${reveal.participantNickname}님이 랜덤으로 정하는 중... 🎲`
                    : `${reveal.participantNickname}님이 선택했어요!`}
              </p>
              {landed && state.status === 'COMPLETED' && (
                <p className="mt-1 text-sm text-primary-600 animate-pop-in">
                  {reveal.stage === 'SUB_CATEGORY' ? '가게가 정해졌어요! 🎉' : '오늘 점심이 정해졌어요! 🎉'}
                </p>
              )}
            </div>
          ) : isMyTurn ? (
            <div className="rounded-2xl bg-primary-500 px-4 py-3 text-center text-white animate-pop-in">
              <p className="text-lg font-extrabold">{labels.mine}</p>
              <p className="text-sm text-primary-50">{labels.mineSub}</p>
            </div>
          ) : (
            <div className="rounded-2xl bg-cream-100 px-4 py-3 text-center">
              <p className="font-bold">
                <span className="text-primary-600">{display.chooserNickname ?? '참여자'}</span>님이 선택 중입니다.
              </p>
              {chooserOffline ? (
                <p className="mt-1 flex items-center justify-center gap-1 text-sm text-amber-700">
                  <WifiOff size={14} aria-hidden="true" />
                  {display.chooserNickname}님이 잠시 연결이 끊어졌어요.
                  {deadline !== null && ` ${deadline}초 후 자동으로 선택돼요.`}
                </p>
              ) : (
                <p className="mt-1 text-sm text-charcoal-500">{labels.others}</p>
              )}
            </div>
          )}
        </div>

        <div className="mt-4">
          <SelectionPath selections={display.selections} />
        </div>

        <div className="mt-5">
          <h2 className="mb-3 text-sm font-semibold text-charcoal-700">
            {isMyTurn ? '원하는 항목을 선택해주세요' : '현재 선택 가능한 항목'}
          </h2>
          <OptionGrid options={display.options} disabled={!canAct} highlightId={highlightId} landed={landed} onSelect={handleSelect} large={stage === 'DINING_TYPE'} />
        </div>

        <div className="mt-5">
          {isMyTurn ? (
            <button type="button" onClick={handleRandom} disabled={!canAct} className="btn-accent btn-lg w-full">
              <Dices size={20} aria-hidden="true" />
              {RANDOM_BUTTON_LABELS[stage].replace('🎲 ', '')}
            </button>
          ) : (
            <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-cream-300 py-3 text-sm text-charcoal-500">
              <span className="h-2 w-2 animate-pulse rounded-full bg-primary-400" aria-hidden="true" />
              {reveal ? '결과 확인 중...' : '선택 대기 중...'}
            </div>
          )}
        </div>
      </div>

      <details className="card p-4">
        <summary className="cursor-pointer text-sm font-semibold text-charcoal-700">
          참여자 {state.participants.filter((p) => p.active).length}명
          {state.maxParticipants > 4 && <span className="ml-2 font-normal text-charcoal-500">· 한 게임에서는 최대 4명이 직접 선택해요</span>}
        </summary>
        <div className="mt-3">
          <ParticipantList participants={state.participants} maxParticipants={0} selfId={selfId} currentParticipantId={reveal ? null : state.currentParticipantId} compact />
        </div>
      </details>

      <div className="flex justify-center">
        <button type="button" onClick={handleLeave} className="btn-ghost btn-sm">
          <LogOut size={16} aria-hidden="true" />
          나가기
        </button>
      </div>
    </div>
  );
}
